const API_BASE = import.meta.env.VITE_API_URL || '';

const AUTH_TOKEN_STORAGE_KEY = 'realplc_auth_token';
const AUTH_REFRESH_TOKEN_STORAGE_KEY = 'realplc_auth_refresh_token';
const AUTH_USER_STORAGE_KEY = 'realplc_auth_user';
const CURRENT_ORG_STORAGE_KEY = 'realplc_current_org_id';
const AUTH_EXPIRES_AT_STORAGE_KEY = 'realplc_auth_expires_at';
const AUTH_EXPIRED_NOTICE_KEY = 'realplc_auth_expired';

export interface AuthUser {
  id: string;
  username?: string;
  email?: string;
  name?: string;
  phone?: string;
  role: 'super_admin' | 'admin' | 'user' | 'viewer';
  organizationId?: string;
  orgRoles?: string[];
  permissions?: string[];
}

type AuthContextResponse = {
  userId: string;
  username?: string;
  email: string;
  name?: string;
  phone?: string;
  organizationId?: string;
  orgRoles: string[];
  permissions: string[];
  platformRoles?: string[];
};

type LoginResponse = {
  ok?: boolean;
  token?: string;
  refreshToken?: string;
  expiresIn?: number;
  auth?: AuthContextResponse;
  message?: string;
};

type OrganizationSummary = {
  id: string;
  name: string;
};

const ADMIN_CONSOLE_PERMISSION = 'audit.read';

function readStorage(key: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(key);
}

function writeStorage(key: string, value: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(key, value);
}

function removeStorage(key: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(key);
}

function mapRole(context: AuthContextResponse): AuthUser['role'] {
  if (context.platformRoles?.includes('platform_super_admin')) {
    return 'super_admin';
  }
  if (context.orgRoles.includes('org_owner') || context.orgRoles.includes('org_admin')) {
    return 'admin';
  }
  if (context.orgRoles.includes('org_engineer') || context.orgRoles.includes('org_operator')) {
    return 'user';
  }
  return 'viewer';
}

function mapAuthUser(context: AuthContextResponse): AuthUser {
  return {
    id: context.userId,
    username: context.username,
    email: context.email,
    name: context.name,
    phone: context.phone,
    role: mapRole(context),
    organizationId: context.organizationId,
    orgRoles: context.orgRoles,
    permissions: context.permissions,
  };
}

function storeAuthUser(user: AuthUser): void {
  writeStorage(AUTH_USER_STORAGE_KEY, JSON.stringify(user));
}

async function fetchAuthContext(token: string): Promise<AuthContextResponse> {
  let response = await fetch(`${API_BASE}/api/auth/me`, {
    headers: createAuthHeaders(token),
  });

  if (response.status === 401) {
    // Attempt token refresh before giving up — prevents logout on page load with stale token
    const refreshed = await refreshTokenIfNeeded();
    if (refreshed) {
      response = await fetch(`${API_BASE}/api/auth/me`, {
        headers: createAuthHeaders(refreshed.token),
      });
    }
  }

  if (response.status === 401) {
    // Reached only when a token existed but could not be revived — a real expiry.
    clearAuthSession();
    markSessionExpired();
    throw new Error('Authentication required');
  }

  if (!response.ok) {
    throw new Error('Failed to fetch current user');
  }

  return await response.json() as AuthContextResponse;
}

async function chooseDefaultOrganization(token: string): Promise<string | null> {
  const response = await fetch(`${API_BASE}/api/organizations`, {
    headers: createAuthHeaders(token),
  });

  if (!response.ok) {
    return null;
  }

  const organizations = await response.json() as OrganizationSummary[];
  if (!Array.isArray(organizations) || organizations.length === 0) {
    return null;
  }

  const organizationId = organizations[0]!.id;
  const switched = await fetch(`${API_BASE}/api/auth/switch-organization`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...createAuthHeaders(token),
    },
    body: JSON.stringify({ organizationId }),
  });

  if (!switched.ok) {
    return null;
  }

  setCurrentOrganizationId(organizationId);
  return organizationId;
}

export function getCurrentOrganizationId(): string | null {
  return readStorage(CURRENT_ORG_STORAGE_KEY);
}

export function setCurrentOrganizationId(organizationId: string | null): void {
  if (!organizationId) {
    removeStorage(CURRENT_ORG_STORAGE_KEY);
    return;
  }

  writeStorage(CURRENT_ORG_STORAGE_KEY, organizationId);
}

export function getAuthToken(): string | null {
  return readStorage(AUTH_TOKEN_STORAGE_KEY);
}

export function getRefreshToken(): string | null {
  return readStorage(AUTH_REFRESH_TOKEN_STORAGE_KEY);
}

export function getAuthUser(): AuthUser | null {
  const value = readStorage(AUTH_USER_STORAGE_KEY);
  if (!value) return null;

  try {
    return JSON.parse(value) as AuthUser;
  } catch {
    removeStorage(AUTH_USER_STORAGE_KEY);
    return null;
  }
}

export function hasAdminConsoleAccess(user: { permissions?: string[]; role?: string } | null): boolean {
  if (user?.role === 'super_admin' || user?.role === 'admin') {
    return true;
  }
  return Boolean(user?.permissions?.includes(ADMIN_CONSOLE_PERMISSION));
}

export function storeAuthSession(
  token: string,
  refreshToken?: string,
  user?: AuthUser,
  expiresIn?: number,
): void {
  writeStorage(AUTH_TOKEN_STORAGE_KEY, token);
  if (refreshToken) {
    writeStorage(AUTH_REFRESH_TOKEN_STORAGE_KEY, refreshToken);
  }
  if (user) {
    storeAuthUser(user);
    setCurrentOrganizationId(user.organizationId || null);
  }
  if (expiresIn && expiresIn > 0) {
    storeExpiresAt(expiresIn);
    scheduleProactiveRefresh(expiresIn);
  }
}

export function clearAuthSession(): void {
  cancelProactiveRefresh();
  removeStorage(AUTH_TOKEN_STORAGE_KEY);
  removeStorage(AUTH_REFRESH_TOKEN_STORAGE_KEY);
  removeStorage(AUTH_USER_STORAGE_KEY);
  removeStorage(CURRENT_ORG_STORAGE_KEY);
  removeStorage(AUTH_EXPIRES_AT_STORAGE_KEY);
}

/**
 * Session death must be visible. A token that cannot be refreshed used to leave
 * the app on the workspace view while every background poll silently retried a
 * dead session — the UI simply looked frozen. Mark the expiry so the next mount
 * can surface "please sign in again" instead of failing quietly.
 */
function markSessionExpired(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.setItem(AUTH_EXPIRED_NOTICE_KEY, '1');
}

/** Reads and clears the expiry marker. Returns true when the session had died. */
export function consumeSessionExpiredNotice(): boolean {
  if (typeof window === 'undefined') return false;
  const flag = window.sessionStorage.getItem(AUTH_EXPIRED_NOTICE_KEY);
  if (flag) window.sessionStorage.removeItem(AUTH_EXPIRED_NOTICE_KEY);
  return Boolean(flag);
}

// Several pollers can hit a dead session in the same tick — redirect only once.
let sessionExpiredRedirectStarted = false;

function redirectToSessionExpiredLogin(): void {
  if (sessionExpiredRedirectStarted) return;
  sessionExpiredRedirectStarted = true;
  clearAuthSession();
  markSessionExpired();
  if (typeof window !== 'undefined') {
    window.location.href = '/';
  }
}

export function createAuthHeaders(token: string | null): Record<string, string> {
  const headers: Record<string, string> = {};

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const organizationId = getCurrentOrganizationId();
  if (organizationId) {
    headers['X-Org-Id'] = organizationId;
  }

  return headers;
}

export async function requestCaptcha(): Promise<never> {
  throw new Error('当前登录流程不再使用验证码，请使用统一认证登录。');
}

export async function loginWithPassword(input: {
  identifier: string;
  password: string;
}): Promise<{ token: string; user: AuthUser }> {
  const response = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      identifier: input.identifier,
      password: input.password,
    }),
  });

  const data = await response.json().catch(() => ({})) as LoginResponse;
  if (!response.ok || !data.token || !data.auth) {
    throw new Error(data.message || '登录失败，请检查账号密码或稍后重试。');
  }

  const user = mapAuthUser(data.auth);
  storeAuthSession(data.token, data.refreshToken, user, data.expiresIn);
  return {
    token: data.token,
    user,
  };
}

export async function fetchCurrentUser(_forceLogin = false): Promise<AuthUser | null> {
  const token = getAuthToken();
  if (!token) {
    return null;
  }

  let context = await fetchAuthContext(token);
  if (!context.organizationId && !context.platformRoles?.includes('platform_super_admin')) {
    await chooseDefaultOrganization(token);
    context = await fetchAuthContext(token);
  }

  const user = mapAuthUser(context);
  storeAuthUser(user);
  setCurrentOrganizationId(user.organizationId || null);
  resumeProactiveRefresh();
  return user;
}

export async function switchOrganization(organizationId: string): Promise<AuthUser> {
  const token = getAuthToken();
  if (!token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE}/api/auth/switch-organization`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...createAuthHeaders(token),
    },
    body: JSON.stringify({ organizationId }),
  });

  if (response.status === 401) {
    clearAuthSession();
    throw new Error('Authentication required');
  }

  if (!response.ok) {
    throw new Error('Failed to switch organization');
  }

  const data = await response.json() as { auth: AuthContextResponse };
  const user = mapAuthUser(data.auth);
  storeAuthUser(user);
  setCurrentOrganizationId(user.organizationId || null);
  return user;
}

export async function logout(): Promise<void> {
  const token = getAuthToken();

  try {
    if (token) {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        headers: createAuthHeaders(token),
      });
    }
  } catch {
    // Ignore logout transport errors and clear local session anyway.
  } finally {
    clearAuthSession();
  }
}

export async function ensureAuthToken(): Promise<string | null> {
  return getAuthToken();
}

// ---------------------------------------------------------------------------
// Proactive token refresh — prevents token expiry during active use
// ---------------------------------------------------------------------------

const PROACTIVE_REFRESH_MARGIN = 0.85; // refresh at 85% of token lifetime

/**
 * Backoff for a renewal that failed transiently. Without it a single unreachable
 * gateway ended the chain for good, and the session then died at the access
 * token's expiry with nothing pending to renew it.
 */
const PROACTIVE_REFRESH_RETRY_DELAYS_MS = [15_000, 60_000, 300_000];

/**
 * How close to expiry a foregrounded tab renews immediately instead of waiting.
 *
 * Must stay well under the access token's lifetime. It was 5 minutes while the
 * gateway issued 5-minute tokens, so `remaining <= threshold` was true from the
 * instant a token was stored, and every `focus` and `visibilitychange` spent a
 * refresh — 20 of them in two minutes on 2026-09-15, which exhausted the org's
 * 60-requests-per-minute budget and 429'd that turn's CODESYS validation.
 *
 * One minute sits just past the proactive schedule, which fires with 15% of the
 * lifetime left (45s of a 300s token): a tab that slept through that point
 * still renews on sight, a tab that was merely clicked away from does not.
 */
const FOREGROUND_RENEWAL_THRESHOLD_MS = 60 * 1000;

let proactiveRefreshTimerId: ReturnType<typeof setTimeout> | null = null;
let proactiveRefreshRetries = 0;
let foregroundWatchInstalled = false;

function getExpiresAt(): number | null {
  const raw = readStorage(AUTH_EXPIRES_AT_STORAGE_KEY);
  if (!raw) return null;
  const ts = Number(raw);
  return Number.isFinite(ts) && ts > 0 ? ts : null;
}

function storeExpiresAt(expiresIn: number): void {
  if (!expiresIn || expiresIn <= 0) return;
  writeStorage(AUTH_EXPIRES_AT_STORAGE_KEY, String(Date.now() + expiresIn * 1000));
}

function scheduleProactiveRefresh(expiresIn: number): void {
  cancelProactiveRefresh();
  if (!expiresIn || expiresIn <= 0) return;

  const delayMs = Math.max(10_000, Math.floor(expiresIn * 1000 * PROACTIVE_REFRESH_MARGIN));
  proactiveRefreshTimerId = setTimeout(() => { void runProactiveRefresh(); }, delayMs);
  installForegroundWatch();
}

function scheduleProactiveRefreshRetry(): void {
  const delayMs = PROACTIVE_REFRESH_RETRY_DELAYS_MS[proactiveRefreshRetries];
  // Backoff exhausted: leave the 401→refresh path to carry the session, which
  // it can only do while the refresh token outlives the access token.
  if (delayMs === undefined) return;

  proactiveRefreshRetries += 1;
  proactiveRefreshTimerId = setTimeout(() => { void runProactiveRefresh(); }, delayMs);
}

async function runProactiveRefresh(): Promise<void> {
  proactiveRefreshTimerId = null;

  // Silent: a transient failure must not end the session — it earns a retry
  // below, and the 401→refresh fallback still backs the whole thing up.
  const result = await refreshTokenIfNeeded({ silentFailure: true }).catch(() => null);

  if (!result) {
    scheduleProactiveRefreshRetry();
    return;
  }

  // A response carrying expiresIn already re-armed the timer through
  // storeAuthSession. Without one nothing is pending, so arm it from the stored
  // expiry rather than letting the loop end on a partial refresh response.
  if (proactiveRefreshTimerId === null) {
    const expiresAt = getExpiresAt();
    const remainingSeconds = expiresAt ? Math.floor((expiresAt - Date.now()) / 1000) : 0;
    if (remainingSeconds > 0) {
      scheduleProactiveRefresh(remainingSeconds);
    }
  }
}

function cancelProactiveRefresh(): void {
  proactiveRefreshRetries = 0;
  if (proactiveRefreshTimerId !== null) {
    clearTimeout(proactiveRefreshTimerId);
    proactiveRefreshTimerId = null;
  }
}

/**
 * A slept, frozen or discarded tab keeps its pending setTimeout while the clock
 * runs on without it, so the renewal it was holding can already be overdue by
 * the time anyone looks again. Re-check against the stored expiry whenever the
 * tab comes back to the foreground.
 */
function handleForeground(): void {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
  if (!getRefreshToken()) return;

  const expiresAt = getExpiresAt();
  if (expiresAt === null) return;

  if (expiresAt - Date.now() <= FOREGROUND_RENEWAL_THRESHOLD_MS) {
    cancelProactiveRefresh();
    void runProactiveRefresh();
    return;
  }

  // Healthy token: only step in when nothing is pending at all.
  if (proactiveRefreshTimerId === null) {
    resumeProactiveRefresh();
  }
}

function installForegroundWatch(): void {
  if (foregroundWatchInstalled) return;
  if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;

  foregroundWatchInstalled = true;
  document.addEventListener('visibilitychange', handleForeground);

  // A machine that slept with this tab still visible fires no visibility
  // change on wake, but the window does regain focus.
  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('focus', handleForeground);
  }
}

/**
 * Re-arm the proactive refresh after a page load. The timer only lives in
 * memory and is scheduled by storeAuthSession, so a reloaded tab previously had
 * no renewal pending at all — it silently rode the access token to expiry and
 * only reacted once a request already came back 401.
 */
export function resumeProactiveRefresh(): void {
  const expiresAt = getExpiresAt();
  if (!expiresAt) return;

  const remainingSeconds = Math.floor((expiresAt - Date.now()) / 1000);
  if (remainingSeconds > 0) {
    scheduleProactiveRefresh(remainingSeconds);
    return;
  }

  // Already past expiry on load — renew now instead of waiting for a 401.
  installForegroundWatch();
  void runProactiveRefresh();
}

let refreshPromise: Promise<{ token: string; refreshToken?: string } | null> | null = null;

export async function refreshTokenIfNeeded(
  opts?: { silentFailure?: boolean },
): Promise<{ token: string; refreshToken?: string } | null> {
  if (refreshPromise) {
    return refreshPromise;
  }

  const currentRefreshToken = getRefreshToken();
  if (!currentRefreshToken) {
    return null;
  }

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refreshToken: currentRefreshToken }),
      });

      // Return null rather than throwing: callers branch on the result to run
      // their own session-expired handling, and a throw used to escape past it.
      if (!response.ok) {
        if (!opts?.silentFailure) {
          clearAuthSession();
        }
        return null;
      }

      const data = await response.json() as LoginResponse;
      if (!data.token || !data.auth) {
        if (!opts?.silentFailure) {
          clearAuthSession();
        }
        return null;
      }

      const user = mapAuthUser(data.auth);
      storeAuthSession(data.token, data.refreshToken, user, data.expiresIn);
      return { token: data.token, refreshToken: data.refreshToken };
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

export async function apiFetch(url: string, init?: RequestInit): Promise<Response> {
  const token = getAuthToken();
  const headers = createAuthHeaders(token);

  const response = await fetch(url, {
    ...init,
    headers: {
      ...init?.headers,
      ...headers,
    },
  });

  if (response.status === 401) {
    // A transport-level failure must not escape either, or the redirect below
    // is skipped and every caller just retries a dead session forever.
    const refreshed = await refreshTokenIfNeeded().catch(() => null);
    if (refreshed) {
      return fetch(url, {
        ...init,
        headers: {
          ...init?.headers,
          ...createAuthHeaders(refreshed.token),
        },
      });
    }

    redirectToSessionExpiredLogin();
    throw new Error('Authentication required');
  }

  return response;
}
